import { Component, OnInit, Input, inject } from '@angular/core';
import { CaseManagementService } from '../../../case-management/case-management.service';
import { PostTradeService } from '../../post-trade.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Platform } from '@angular/cdk/platform';
import { saveAs } from '@progress/kendo-file-saver';
import { EnvironmentService } from '../../../services/environment.service';
import uniqBy from 'lodash-es/uniqBy';
import { TradeResponse } from '../../../models/post-trade-models';

@Component({
  selector: 'app-attachments-widget',
  templateUrl: './attachments-widget.component.html',
  styleUrls: ['./attachments-widget.component.scss'],
  standalone: false,
})
export class AttachmentsWidgetComponent implements OnInit {
  cms = inject(CaseManagementService);
  ptSvc = inject(PostTradeService);
  private snacky = inject(MatSnackBar);
  private pltfrm = inject(Platform);
  private envSvc = inject(EnvironmentService);

  @Input() tradeDetail;
  @Input() tradeId;
  @Input() widgetType;
  @Input() readOnly;
  baseUrl: string;
  environment;
  note = '';
  fileName;
  fileNameDisplay;
  file;
  fileType;
  caseFileCols = [
    {
      varName: 'CreateDate',
      label: 'Date',
    },
    {
      varName: 'NoteText',
      label: 'Note',
    },
    {
      varName: 'CreatedByUserId',
      label: 'Reviewer ID',
    },
    {
      varName: 'Name',
      label: 'Attachments',
    },
  ];
  relatedCases = [];
  tradeAttachments = [];
  caseType;
  caseTitle;
  relatedCaseTypes;

  ngOnInit() {
    this.environment = this.envSvc.get();
    this.baseUrl = this.environment.apiBase;

    this.caseType = 'TradeInternal';
    this.relatedCaseTypes = ['TradeInternal', 'TradeBulk'];

    if (this.widgetType === 'account') {
      this.caseType = 'AccountInternal';
      this.relatedCaseTypes = ['AccountInternal', 'AccountBulk'];
    } else if (this.widgetType === 'rep') {
      this.caseType = 'RepInternal';
      this.relatedCaseTypes = ['RepInternal', 'RepBulk'];
    } else if (this.widgetType === 'aml') {
      this.caseType = 'AMLInternal';
      this.relatedCaseTypes = ['AMLInternal', 'AMLBulk'];
    }

    this.caseTitle = this.tradeId;
    this.getNotesAndFiles();
  }

  getNotesAndFiles() {
    this.cms
      .getRelatedCases(this.tradeId, this.relatedCaseTypes, 'rb')
      .subscribe(resp => {
        if (resp['length'] > 0) {
          const cases = Object.keys(resp);
          const caseIds = [];

          cases.map(idx => {
            this.relatedCases.push(resp[idx]);
            caseIds.push(resp[idx].CRID);
          });

          this.cms
            .getCaseNotesAndFiles(JSON.stringify(caseIds))
            .subscribe(filesResp => {
              if (filesResp['length'] > 0) {
                const formattedAttachments = [];
                for (const index in filesResp) {
                  const relatedFile = formattedAttachments.find(
                    formattedAttachment =>
                      formattedAttachment.NoteId === filesResp[index]['NoteId']
                  );
                  if (!relatedFile) {
                    formattedAttachments.push(filesResp[index]);
                  } else {
                    const mergedFile = Object.assign(
                      relatedFile,
                      filesResp[index]
                    );
                    formattedAttachments.push(mergedFile);
                  }
                }

                this.tradeAttachments = uniqBy(formattedAttachments, 'NoteId');
              }
            });
        }
      });
  }

  onFileSelected(event) {
    this.file = event.target.files[0];

    if (this.file) {
      this.fileName = this.file.name;
    }
  }

  clearFile() {
    this.fileName = null;
    this.fileNameDisplay = null;
    this.file = null;
    this.fileType = null;
  }

  save() {
    const caseData = {
      CaseManagement: {
        Type: this.caseType,
        Title: this.tradeId,
      },
      CaseToProfile: {
        ProfileID: [this.tradeId],
      },
      Client_Rep: {
        RepCode: [this.tradeDetail.RepCode],
      },
    };
    if (
      this.relatedCases.length === 0 ||
      !this.relatedCases?.find(
        relatedCase => relatedCase.Type === 'TradeInternal'
      )
    ) {
      this.cms
        .createCaseManagementRecord(null, caseData)
        .subscribe(createCaseResp => {
          if (createCaseResp['result']) {
            this.saveNotesAndAttachments(createCaseResp['result']);
          }
        });
    } else {
      this.relatedCases.map(relatedCase => {
        if (relatedCase.Type === 'TradeInternal') {
          this.saveNotesAndAttachments(relatedCase.CRID);
        }
      });
    }
  }

  updateTrade(configData) {
    this.ptSvc.updateTrade(configData).subscribe((resp: TradeResponse) => {
      if (resp.crid) {
        this.saveNotesAndAttachments(resp.crid);
      }
    });
  }

  saveNotesAndAttachments(caseId) {
    if (this.note !== '' && this.note !== null && !this.fileName) {
      const note = {
        CaseRecordId: caseId,
        NoteText: this.note,
        NoteTypeId: 2,
      };

      this.cms.addCaseRecordNote(note).subscribe({
        next: () => {
          this.note = '';
          this.openSnacky('Note saved');
          this.getNotesAndFiles();
        },
        error: error => {
          this.openSnacky(`Error: ${error}`);
        },
      });
    } else if (this.fileName && (this.note === '' || this.note === null)) {
      const formData = new FormData();
      formData.append('File1', this.file);
      this.fileName = '';

      this.cms.saveCaseRecordFile(caseId, formData).subscribe({
        next: () => {
          this.openSnacky('File uploaded.');
          this.getNotesAndFiles();
        },
        error: error => {
          this.openSnacky(`Error: ${error}`);
        },
      });
    } else {
      const formData = new FormData();
      formData.append('File1', this.file);
      this.fileName = '';
      this.cms.saveCaseRecordFile(caseId, formData).subscribe({
        next: resp => {
          if (resp['id']) {
            this.cms
              .addCaseRecordNote({
                CaseRecordId: caseId,
                CaseRecordFileId: resp['id'],
                NoteText: this.note,
                NoteTypeId: 2,
              })
              .subscribe({
                next: () => {
                  this.note = '';
                  this.openSnacky('File uploaded & note saved.');
                  this.getNotesAndFiles();
                },
                error: error => {
                  this.openSnacky(`Error: ${error}`);
                },
              });
          }
        },
        error: error => {
          this.openSnacky(`Error: ${error}`);
        },
      });
    }
  }

  openSnacky(message) {
    this.snacky.open(message, 'Close', {
      duration: 3000,
    });
  }

  downloadAttachment(fileDetails) {
    this.cms.getCaseRecordFile(fileDetails.id).subscribe(resp => {
      const filename = `${fileDetails.Name}`;
      const iosSafari =
        this.pltfrm.IOS ||
        (navigator?.['userAgentData']?.platform === 'macOS' &&
          navigator.maxTouchPoints > 1);
      let fileData;
      const reader = new FileReader();
      reader.readAsDataURL(resp);
      reader.onloadend = () => {
        fileData = reader.result;
        saveAs(fileData, filename, {
          proxyURL: `${this.baseUrl}/util/proxy`,
          proxyTarget: '_self',
          forceProxy: iosSafari,
        });
      };
    });
  }
}
